import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export default function AdminPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['boardMembers'],
    queryFn: api.listBoardMembers,
  });

  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'member' });
  const [newInvite, setNewInvite] = useState(null);

  const createMutation = useMutation({
    mutationFn: (payload) => api.createBoardMember(payload),
    onSuccess: (result) => {
      setNewInvite(result);
      setForm({ name: '', email: '', phone: '', role: 'member' });
      queryClient.invalidateQueries({ queryKey: ['boardMembers'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => api.revokeBoardMember(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['boardMembers'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id) => api.activateBoardMember(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['boardMembers'] }),
  });

  const regenMutation = useMutation({
    mutationFn: (id) => api.regenerateInviteToken(id),
    onSuccess: (result) => setNewInvite(result),
  });

  function handleCreate(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    createMutation.mutate(form);
  }

  function copy(text) {
    navigator.clipboard?.writeText(text);
  }

  if (isLoading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error.message}</div>;

  const members = data?.board_members || [];

  return (
    <div className="max-w-4xl mx-auto p-6 overflow-y-auto h-full">
      <h1 className="text-2xl font-bold text-ccf-primary mb-1">Admin — Board Members</h1>
      <p className="text-sm text-gray-500 mb-6">
        Invite new board members. Each member gets a unique invite link that logs them in for 90 days.
      </p>

      {/* Create form */}
      <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Invite new board member</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@example.com"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="602-555-1234"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            type="submit"
            disabled={createMutation.isPending || !form.name.trim()}
            className="bg-ccf-primary text-white rounded px-4 py-2 text-sm hover:bg-blue-900 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create invite'}
          </button>
        </div>
      </form>

      {/* New invite callout */}
      {newInvite && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-6">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-sm font-semibold text-emerald-900">
                Invite created{newInvite.name ? ` for ${newInvite.name}` : ''}
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">
                Send this link to them. It logs them in and keeps them logged in for 90 days.
              </div>
            </div>
            <button
              onClick={() => setNewInvite(null)}
              className="text-emerald-700 hover:text-emerald-900 text-xl leading-none"
            >
              ×
            </button>
          </div>
          <div className="flex gap-2">
            <input
              readOnly
              value={newInvite.invite_url}
              className="flex-1 border border-emerald-300 rounded px-2 py-1.5 text-xs bg-white font-mono"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={() => copy(newInvite.invite_url)}
              className="bg-emerald-600 text-white rounded px-3 py-1.5 text-xs hover:bg-emerald-700"
            >
              Copy
            </button>
            {newInvite.email && (
              <a
                href={`mailto:${newInvite.email}?subject=${encodeURIComponent(
                  'Your CCF Board Dashboard invite'
                )}&body=${encodeURIComponent(
                  `Click this link to sign in to the CCF Board Dashboard:\n\n${newInvite.invite_url}\n\nIt will keep you signed in for 90 days.`
                )}`}
                className="bg-white border border-emerald-300 text-emerald-700 rounded px-3 py-1.5 text-xs hover:bg-emerald-50"
              >
                Email
              </a>
            )}
          </div>
        </div>
      )}

      {/* Members table */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Last login</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t border-gray-100">
                <td className="px-4 py-2 font-medium text-gray-900">{m.name}</td>
                <td className="px-4 py-2 text-gray-600">{m.email || '—'}</td>
                <td className="px-4 py-2">
                  {m.role === 'admin' ? (
                    <span className="text-xs bg-ccf-primary text-white rounded px-2 py-0.5">admin</span>
                  ) : (
                    <span className="text-xs text-gray-600">member</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">
                  {m.last_login ? new Date(m.last_login + 'Z').toLocaleDateString() : 'Never'}
                </td>
                <td className="px-4 py-2">
                  {m.active ? (
                    <span className="text-xs text-emerald-700">Active</span>
                  ) : (
                    <span className="text-xs text-gray-400">Revoked</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right space-x-2">
                  <button
                    onClick={() => regenMutation.mutate(m.id)}
                    className="text-xs text-ccf-primary hover:underline"
                    disabled={regenMutation.isPending}
                  >
                    New link
                  </button>
                  {m.active ? (
                    <button
                      onClick={() => revokeMutation.mutate(m.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Revoke
                    </button>
                  ) : (
                    <button
                      onClick={() => activateMutation.mutate(m.id)}
                      className="text-xs text-emerald-600 hover:underline"
                    >
                      Activate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
